import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { getMetaTags } from './api';
import {
  adminCreateMetaTag,
  adminDeleteMetaTag,
  adminGetMetaTags,
  adminUpdateMetaTag,
} from './adminApi';

export interface IMetaTag {
  _id: string;
  path: string;
  title: string;
  description: string;
}

export interface IInitialMetaTagsState {
  status: 'idle' | 'loading' | 'rejected';
  metaTagListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IMetaTag>;
  };
  // Admin
  adminMetaTagsListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IMetaTag>;
  };
}

const initialState: IInitialMetaTagsState = {
  status: 'idle',
  metaTagListData: undefined,
  // Admin
  adminMetaTagsListData: undefined,
};

export const MetaTagsSlice = createSlice({
  initialState,
  name: 'metaTags',
  reducers: {
    resetMetaTagListData: (state) => {
      state.metaTagListData = undefined;
    },
    // Admin
    resetAdminMetaTagsListData: (state) => {
      state.adminMetaTagsListData = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getMetaTags.fulfilled, (state, action) => {
      const result = action.payload;

      const newMetaTagListData = {
        limit: result.limit,
        offset: result.offset,
        totalDocs: result.totalDocs || 0,
        list: result.docs || [],
      };

      state.metaTagListData = newMetaTagListData;
    });

    builder
      // Admin
      .addCase(adminGetMetaTags.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminMetaTagsListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminMetaTagsListData = newAdminMetaTagsListData;
      })
      .addCase(adminCreateMetaTag.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminMetaTagsListData) {
          const newAdminMetaTagsListData = {
            ...state.adminMetaTagsListData,
          };

          newAdminMetaTagsListData.list.unshift(result);

          if (newAdminMetaTagsListData.totalDocs) {
            newAdminMetaTagsListData.totalDocs += 1;
          } else {
            newAdminMetaTagsListData.totalDocs = 1;
          }

          state.adminMetaTagsListData = newAdminMetaTagsListData;
        }
      })
      .addCase(adminUpdateMetaTag.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminMetaTagsListData &&
          state.adminMetaTagsListData.list &&
          state.adminMetaTagsListData.list.length
        ) {
          const newAdminMetaTagsListData = {
            ...state.adminMetaTagsListData,
          };

          const index = newAdminMetaTagsListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminMetaTagsListData.list[index] = result;
          }

          state.adminMetaTagsListData = newAdminMetaTagsListData;
        }
      })
      .addCase(adminDeleteMetaTag.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminMetaTagsListData &&
          state.adminMetaTagsListData.list &&
          state.adminMetaTagsListData.list.length
        ) {
          const newAdminMetaTagsListData = {
            ...state.adminMetaTagsListData,
          };

          newAdminMetaTagsListData.totalDocs = newAdminMetaTagsListData.totalDocs
            ? newAdminMetaTagsListData.totalDocs - 1
            : 0;

          newAdminMetaTagsListData.list = newAdminMetaTagsListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminMetaTagsListData = newAdminMetaTagsListData;
        }
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateMetaTag,
        adminGetMetaTags,
        adminUpdateMetaTag,
        adminDeleteMetaTag
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateMetaTag,
        adminGetMetaTags,
        adminUpdateMetaTag,
        adminDeleteMetaTag
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateMetaTag,
        adminGetMetaTags,
        adminUpdateMetaTag,
        adminDeleteMetaTag
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getMetaTagsState = (state: RootState): RootState['metaTags'] =>
  state.metaTags;

export const {
  resetMetaTagListData,

  // Admin
  resetAdminMetaTagsListData,
} = MetaTagsSlice.actions;

export * from './adminApi';
export default MetaTagsSlice.reducer;
